import { useCallback, useEffect, useState } from 'react';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import type { IsoDate } from '@lemontaigu/pms-core';
import { usePmsReach } from './PmsProvider.js';
import { fmtDate, isoAddDays } from './logic.js';

// OB6 — « Passage de jour » by hand, for an établissement whose day is the
// PMS's own (training now; every établissement once live). In proof the day
// follows Vega and the database refuses a manual passage, so the control is
// not offered there. C4: the database refuses while an arrival of the day is
// unresolved, and names them — the message is shown as it comes.

export function DayPassage({ onDone }: { onDone: () => void }) {
  const can = useCapability(CAPABILITIES.PMS_DAY_CLOSE);
  const reach = usePmsReach();
  const own = reach.filter((b) => b.property.mode === 'training' || b.property.mode === 'live');
  const [days, setDays] = useState<Record<string, IsoDate | null>>({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const out: Record<string, IsoDate | null> = {};
    for (const b of reach) {
      if (b.property.mode !== 'training' && b.property.mode !== 'live') continue;
      const r = await b.services.reservations.currentDay(b.property.code);
      out[b.property.code] = r.ok ? r.value : null;
    }
    setDays(out);
  }, [reach]);

  useEffect(() => {
    if (can && own.length > 0) void load();
  }, [can, own.length, load]);

  if (!can || own.length === 0) return null;

  const pass = async (code: string, label: string, day: IsoDate) => {
    const next = isoAddDays(day, 1);
    if (!window.confirm(`Passer « ${label} » du ${fmtDate(day)} au ${fmtDate(next)} ?`)) return;
    const bundle = own.find((b) => b.property.code === code)!;
    setBusy(true);
    setMsg('');
    const r = await bundle.services.reservations.closeDay(code, day);
    setBusy(false);
    if (!r.ok) return setMsg(r.error.message);
    setMsg(`${label} : jour courant ${fmtDate(r.value)}.`);
    await load();
    onDone();
  };

  return (
    <div className="pms-training-reset">
      {own.map((b) => {
        const day = days[b.property.code] ?? null;
        return (
          <span key={b.property.code} className="pms-hint">
            {b.property.labelFr} — jour courant {day ? fmtDate(day) : '…'}{' '}
            {day && (
              <button
                type="button"
                className="pms-chip"
                disabled={busy}
                onClick={() => void pass(b.property.code, b.property.labelFr, day)}
              >
                Passage au {fmtDate(isoAddDays(day, 1))}
              </button>
            )}
          </span>
        );
      })}
      {msg && <span className="pms-hint">{msg}</span>}
    </div>
  );
}
